import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useTickets } from '../../hooks/useTickets';
import { useTicketWebSocket } from '../../hooks/useTicketWebSocket';
import { TicketRowItem } from '../common/TicketRowItem';
import { SortableTicketRow } from '../common/SortableTicketRow';
import { TicketRowSkeletonList } from '../common/TicketRowSkeleton';
import { Pagination } from '../common/Pagination';
import type { Ticket } from '../../types';

export type TicketTab = 'new' | 'in_progress' | 'resolved';
export type TicketSort = 'unread' | 'newest' | 'oldest' | 'activity';
/** Sort options for the Resolved tab — rows are users, not tickets, so
 *  the available dimensions differ from TicketSort. */
export type ResolvedSort = 'most_recent' | 'most_tickets' | 'name_az';
export type DateRange = { from?: string; to?: string };

interface TicketListProps {
  initialTab?: TicketTab;
  listPages: Record<TicketTab, number>;
  onListPagesChange: Dispatch<SetStateAction<Record<TicketTab, number>>>;
  /** Sort state for New / In-Progress tabs (ticket-level sort). */
  listSort: TicketSort;
  onListSortChange: Dispatch<SetStateAction<TicketSort>>;
  /** Sort state for Resolved tab (user-level sort). Kept separate from
   *  listSort because the option lists don't overlap. */
  resolvedSort: ResolvedSort;
  onResolvedSortChange: Dispatch<SetStateAction<ResolvedSort>>;
  /** Date range + search query are shared across all three tabs so toggling
   *  tabs preserves the user's selections. On Resolved, the date range
   *  bounds `closed_at`; on other tabs it bounds `created_at`. */
  listDateRange: DateRange;
  onListDateRangeChange: Dispatch<SetStateAction<DateRange>>;
  listQuery: string;
  onListQueryChange: Dispatch<SetStateAction<string>>;
  /** Resolved-only filter: category slugs the user's tickets must include. */
  listCategories: string[];
  onListCategoriesChange: Dispatch<SetStateAction<string[]>>;
  /** Resolved-only filter: show only users who messaged after their latest
   *  closed_at (i.e. came back after we closed their issue). */
  listHasFollowup: boolean;
  onListHasFollowupChange: Dispatch<SetStateAction<boolean>>;
  /** Keeps parent in sync so returning from ticket detail restores the active tab */
  onTabChange?: (tab: TicketTab) => void;
  onSelectTicket: (ticket: Ticket) => void;
}

const tabLabels: Record<TicketTab, string> = {
  new: 'New Tickets',
  in_progress: 'In Progress',
  resolved: 'Resolved',
};

const sortLabels: Record<TicketSort, string> = {
  unread: 'Unread first',
  newest: 'Newest',
  oldest: 'Oldest',
  activity: 'Last activity',
};

const resolvedSortLabels: Record<ResolvedSort, string> = {
  most_recent: 'Most recent',
  most_tickets: 'Most tickets',
  name_az: 'Name A→Z',
};

const DATE_PRESETS = [
  { id: 'all', label: 'All time' },
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last7', label: 'Last 7 days' },
  { id: 'last30', label: 'Last 30 days' },
] as const;
type PresetId = (typeof DATE_PRESETS)[number]['id'];

/** Local-time YYYY-MM-DD (toISOString would shift across midnight in non-UTC zones). */
function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function presetToRange(preset: PresetId): DateRange {
  if (preset === 'all') return {};
  const today = new Date();
  const todayStr = isoDate(today);
  if (preset === 'today') return { from: todayStr, to: todayStr };
  if (preset === 'yesterday') {
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    const yStr = isoDate(y);
    return { from: yStr, to: yStr };
  }
  const days = preset === 'last7' ? 6 : 29;
  const start = new Date(today);
  start.setDate(start.getDate() - days);
  return { from: isoDate(start), to: todayStr };
}

/** Compact date for the Resolved tab's match-context line. Falls back to
 *  the raw string if it can't be parsed — better than rendering "Invalid
 *  Date". */
function formatMatchedDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatRangeLabel(range: DateRange): string {
  if (!range.from && !range.to) return '';
  if (range.from === range.to && range.from) {
    const today = isoDate(new Date());
    if (range.from === today) return 'Today';
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (range.from === isoDate(yesterday)) return 'Yesterday';
    return range.from;
  }
  if (range.from && range.to) return `${range.from} → ${range.to}`;
  if (range.from) return `From ${range.from}`;
  return `Until ${range.to}`;
}

/** Is any ticket-tab filter dimension non-default? Used to color the filter
 *  pill, decide whether to show the chip row, etc. Per-tab because the
 *  Resolved tab has filter dimensions that don't exist on other tabs. */
function isFilterActive(
  tab: TicketTab,
  ticketSort: TicketSort,
  resolvedSort: ResolvedSort,
  range: DateRange,
  query: string,
  categories: string[],
  hasFollowup: boolean,
): boolean {
  const sharedActive =
    Boolean(range.from || range.to) || Boolean(query.trim());
  if (tab === 'resolved') {
    return (
      sharedActive ||
      resolvedSort !== 'most_recent' ||
      categories.length > 0 ||
      hasFollowup
    );
  }
  return sharedActive || ticketSort !== 'unread';
}

/** Debounce window for live search — short enough to feel responsive,
 *  long enough that fast typists don't fire a fetch per keystroke. */
const SEARCH_DEBOUNCE_MS = 300;

export function TicketList({
  initialTab = 'new',
  listPages,
  onListPagesChange,
  listSort,
  onListSortChange,
  resolvedSort,
  onResolvedSortChange,
  listDateRange,
  onListDateRangeChange,
  listQuery,
  onListQueryChange,
  listCategories,
  onListCategoriesChange,
  listHasFollowup,
  onListHasFollowupChange,
  onTabChange,
  onSelectTicket,
}: TicketListProps) {
  const tabs: TicketTab[] = ['new', 'in_progress', 'resolved'];
  const [activeTab, setActiveTab] = useState<TicketTab>(initialTab);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  const [slideDir, setSlideDir] = useState<'left' | 'right'>('right');
  /** True after user changes list page via pager — keeps overlay on New / Resolved even when stale rows filter empty mid-fetch. */
  const [pagingInFlight, setPagingInFlight] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  // Filter state is global — `activeSort` / `activeRange` / `committedQuery`
  // are just aliases for the parent-owned values. Switching tabs no longer
  // swaps these.
  const activeSort = listSort;
  const activeRange = listDateRange;
  const committedQuery = listQuery;

  // Local input mirror so typing is responsive — committed (debounced) query
  // is what actually fires the fetch via `onListQueryChange`.
  const [queryDraft, setQueryDraft] = useState(committedQuery);

  // Re-sync draft when the committed query changes from elsewhere (e.g.
  // "Clear all" button, programmatic clear). When the user is typing, the
  // condition below short-circuits.
  useEffect(() => {
    setQueryDraft(committedQuery);
  }, [committedQuery]);

  // Filter changes affect ALL tabs (state is global), so reset every tab's
  // page to 1 — the previous page number can't be assumed to exist after
  // the result set narrows.
  const resetAllPages = useCallback(() => {
    onListPagesChange({ new: 1, in_progress: 1, resolved: 1 });
  }, [onListPagesChange]);

  // Debounce: push the draft to parent state SEARCH_DEBOUNCE_MS after the
  // last keystroke. Skip when draft already matches committed (no-op echo
  // from the sync effect above).
  useEffect(() => {
    if (queryDraft === committedQuery) return;
    const id = setTimeout(() => {
      onListQueryChange(queryDraft);
      onListPagesChange({ new: 1, in_progress: 1, resolved: 1 });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [queryDraft, committedQuery, onListQueryChange, onListPagesChange]);

  const setActiveSort = useCallback((next: TicketSort) => {
    onListSortChange(next);
    resetAllPages();
  }, [onListSortChange, resetAllPages]);

  const setActiveResolvedSort = useCallback((next: ResolvedSort) => {
    onResolvedSortChange(next);
    resetAllPages();
  }, [onResolvedSortChange, resetAllPages]);

  const setActiveRange = useCallback((next: DateRange) => {
    onListDateRangeChange(next);
    resetAllPages();
  }, [onListDateRangeChange, resetAllPages]);

  const setActiveCategories = useCallback((next: string[]) => {
    onListCategoriesChange(next);
    resetAllPages();
  }, [onListCategoriesChange, resetAllPages]);

  const toggleCategory = useCallback((id: string) => {
    onListCategoriesChange((prev) => prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]);
    resetAllPages();
  }, [onListCategoriesChange, resetAllPages]);

  const setActiveHasFollowup = useCallback((next: boolean) => {
    onListHasFollowupChange(next);
    resetAllPages();
  }, [onListHasFollowupChange, resetAllPages]);

  const clearActiveQuery = useCallback(() => {
    setQueryDraft('');
    onListQueryChange('');
    resetAllPages();
  }, [onListQueryChange, resetAllPages]);

  const clearAllFilters = useCallback(() => {
    setActiveSort('unread');
    setActiveResolvedSort('most_recent');
    setActiveRange({});
    setActiveCategories([]);
    setActiveHasFollowup(false);
    clearActiveQuery();
  }, [setActiveSort, setActiveResolvedSort, setActiveRange, setActiveCategories, setActiveHasFollowup, clearActiveQuery]);

  const changeTab = (next: TicketTab) => {
    const currentIndex = tabs.indexOf(activeTab);
    const nextIndex = tabs.indexOf(next);
    setSlideDir(nextIndex > currentIndex ? 'right' : 'left');
    setActiveTab(next);
    onTabChange?.(next);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
    const currentIndex = tabs.indexOf(activeTab);
    // Swipe left → next tab, swipe right → previous tab (standard mobile convention)
    if (dx < 0 && currentIndex < tabs.length - 1) {
      changeTab(tabs[currentIndex + 1]);
    } else if (dx > 0 && currentIndex > 0) {
      changeTab(tabs[currentIndex - 1]);
    }
  };

  const {
    openTickets,
    openPagination,
    archivedConversations,
    archivedPagination,
    isLoading,
    error,
    stats,
    categories: allCategories,
    refreshOpenTickets,
    loadArchivedConversations,
    loadStats,
    loadCategories,
    pinTicket,
    unpinTicket,
    reorderPins,
  } = useTickets();

  // Per-ticket pin-in-flight flag so the user can't double-tap into a half
  // pin/unpin state. Keyed by ticketId; cleared on completion. (Set lives at
  // this scope; handlers are defined below `refetchListAndStats` to avoid a
  // temporal-dead-zone reference.)
  const [pinningIds, setPinningIds] = useState<Set<string>>(new Set());
  const setPinning = useCallback((id: string, inFlight: boolean) => {
    setPinningIds((prev) => {
      const next = new Set(prev);
      if (inFlight) next.add(id); else next.delete(id);
      return next;
    });
  }, []);

  // Drag-and-drop sensors. PointerSensor handles mouse + pen, TouchSensor
  // covers iOS WKWebView. activationConstraint.distance prevents a stray
  // pixel of finger jitter from being interpreted as a drag (which would
  // otherwise eat the long-press → menu gesture on the row body).
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
  );

  // Fetch the category list once on mount — used by the Resolved tab's
  // category multi-select. Cheap (small JSON, no auth weirdness).
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Map the active tab to the status that scopes the backend fetch. 'resolved'
  // uses a different endpoint (archived) so it returns undefined here.
  const tabStatus: 'open' | 'in_progress' | undefined =
    activeTab === 'new' ? 'open' : activeTab === 'in_progress' ? 'in_progress' : undefined;

  const refetchListAndStats = useCallback(() => {
    // Stats fetch carries the same filter args so all tab badges reflect
    // the filtered counts — no need to visit a tab to see its real number.
    loadStats({
      from: activeRange.from,
      to: activeRange.to,
      q: committedQuery,
    });
    // Archived conversations fetch — only fires when the Resolved tab is
    // active OR has just been switched away from but the query string still
    // matters for the badge. Issue #100/#102: previously fired on EVERY
    // refresh (including WS events / 30s poll while the user was on New or
    // In-Progress) which ran the heavier 5-CTE query for a tab the user
    // wasn't even looking at. Tradeoff: the Resolved-tab badge now shows
    // the last-known value when the user is on a different tab; switching
    // to Resolved re-fetches naturally because activeTab is in the dep list.
    if (activeTab === 'resolved') {
      loadArchivedConversations({
        page: listPages.resolved,
        q: committedQuery,
        from: activeRange.from,
        to: activeRange.to,
        categories: listCategories.length > 0 ? listCategories : undefined,
        hasFollowup: listHasFollowup || undefined,
        sort: resolvedSort,
      });
    }
    if (tabStatus) {
      const page = activeTab === 'new' ? listPages.new : listPages.in_progress;
      refreshOpenTickets({
        status: tabStatus,
        page,
        sort: activeSort,
        from: activeRange.from,
        to: activeRange.to,
        q: committedQuery,
      });
    }
  }, [
    activeTab,
    tabStatus,
    activeSort,
    resolvedSort,
    activeRange.from,
    activeRange.to,
    committedQuery,
    listCategories,
    listHasFollowup,
    listPages.new,
    listPages.in_progress,
    listPages.resolved,
    loadArchivedConversations,
    loadStats,
    refreshOpenTickets,
  ]);

  // Pin/unpin handlers — defined here (after refetchListAndStats) so the
  // closure captures the stable refetch reference.
  const handlePin = useCallback(async (ticketId: string) => {
    setPinning(ticketId, true);
    const result = await pinTicket(ticketId);
    setPinning(ticketId, false);
    if (!result.ok) {
      // 100-pin cap reached or network error — surface as a simple alert
      // for v1; could become a toast in a follow-up polish PR.
      alert(result.error);
      return;
    }
    // Re-fetch so the server's pinned-first order applies (the local flip
    // already set isPinned=true; this re-sorts the array).
    refetchListAndStats();
  }, [pinTicket, setPinning, refetchListAndStats]);

  const handleUnpin = useCallback(async (ticketId: string) => {
    setPinning(ticketId, true);
    const result = await unpinTicket(ticketId);
    setPinning(ticketId, false);
    if (!result.ok) {
      alert(result.error);
      return;
    }
    refetchListAndStats();
  }, [unpinTicket, setPinning, refetchListAndStats]);

  // Reorder handler — fires on drop. dnd-kit gives us active.id (the dragged
  // ticket) + over.id (the row it was dropped on). We compute the new order
  // locally from the current pinned list and ship to the server, then
  // re-fetch so the server's canonical order is reflected.
  const handlePinDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const sourceList: Ticket[] = activeTab === 'resolved'
        ? (Array.isArray(archivedConversations) ? archivedConversations : []).map((r) => r.ticket)
        : (Array.isArray(openTickets) ? openTickets : []);
      const pinnedList = sourceList.filter((t) => t.isPinned).map((t) => t.ticketId);
      const oldIndex = pinnedList.indexOf(String(active.id));
      const newIndex = pinnedList.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;
      const reordered = arrayMove(pinnedList, oldIndex, newIndex);

      const result = await reorderPins(reordered);
      if (!result.ok) {
        alert(result.error);
      }
      refetchListAndStats();
    },
    [activeTab, openTickets, archivedConversations, reorderPins, refetchListAndStats],
  );

  useEffect(() => {
    refetchListAndStats();
  }, [refetchListAndStats]);

  useEffect(() => {
    if (!isLoading) {
      setPagingInFlight(false);
    }
  }, [isLoading]);

  const token = localStorage.getItem('support_auth_token') || '';

  const { lastMessage: wsMessage } = useTicketWebSocket({
    ticketId: 'stats',
    authToken: token,
    enabled: true,
    onOpen: () => {
      refetchListAndStats();
    },
  });

  useEffect(() => {
    if (wsMessage && (wsMessage.type === 'new_ticket' || wsMessage.type === 'ticket_updated' || wsMessage.type === 'stats_updated')) {
      refetchListAndStats();
    }
  }, [wsMessage, refetchListAndStats]);

  useEffect(() => {
    const id = setInterval(() => {
      refetchListAndStats();
    }, 30_000);
    return () => clearInterval(id);
  }, [refetchListAndStats]);

  // Client-side filter remains as a transition guard: between a tab switch
  // and the next fetch resolving, `openTickets` still holds the previous
  // tab's rows. Filtering by the active status hides them until the fresh
  // fetch arrives (the loading spinner shows during that window).
  const ticketsForTab = tabStatus
    ? (Array.isArray(openTickets) ? openTickets : []).filter((t) => t.status === tabStatus)
    : [];

  const filterActive = isFilterActive(
    activeTab,
    activeSort,
    resolvedSort,
    activeRange,
    committedQuery,
    listCategories,
    listHasFollowup,
  );

  // Memo'd label lookup for the active category chips. Falls back to the slug
  // if the catalog hasn't arrived yet (initial mount race).
  const categoryLabelById = useMemo(() => {
    const map = new Map<string, string>();
    (allCategories ?? []).forEach((c) => map.set(c.id, c.label));
    return map;
  }, [allCategories]);

  // Tab badge counts. New / In-Progress come from /api/tickets/stats (shared
  // filter params). Resolved prefers archivedPagination.totalTickets — that
  // count reflects Resolved-tab-only filters (category, hasFollowup) which
  // /stats doesn't honor. Falls back to stats.closed when archived hasn't
  // loaded yet (e.g. first mount, before the user has clicked Resolved) so
  // the badge shows the real number instead of 0.
  // Counted in TICKETS, not users (one user with 3 closed tickets adds 3).
  const tabCounts: Record<TicketTab, number | undefined> = {
    new: stats?.open,
    in_progress: stats?.inProgress,
    resolved: archivedPagination.totalTickets ?? stats?.closed,
  };

  const handleOpenPageChange = (page: number) => {
    setPagingInFlight(true);
    const key: 'new' | 'in_progress' = activeTab === 'new' ? 'new' : 'in_progress';
    onListPagesChange((p) => ({ ...p, [key]: page }));
  };

  const handleResolvedPageChange = (page: number) => {
    setPagingInFlight(true);
    onListPagesChange((p) => ({ ...p, resolved: page }));
  };

  const emptyForTab =
    activeTab === 'resolved'
      ? !Array.isArray(archivedConversations) || archivedConversations.length === 0
      : ticketsForTab.length === 0;
  const showOpenPager = activeTab !== 'resolved' && openPagination.totalPages > 1;
  const showResolvedPager = activeTab === 'resolved' && archivedPagination.totalPages > 1;

  if (error) {
    return (
      <div className="p-3 sm:p-6 flex flex-col gap-6">
        <div className="flex items-center justify-center min-h-[40vh]">
          <div className="text-center">
            <div className="text-text-danger mb-4">{error}</div>
            <button
              onClick={() => refetchListAndStats()}
              className="px-4 py-2 bg-text-brand text-white rounded-lg text-sm"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 flex flex-col flex-1 min-h-0 gap-6">
      {/* Header block — fixed height; does not scroll with the list */}
      <div className="flex flex-col gap-4 shrink-0">
        {/* Title */}
        <h1 className="text-xl font-bold text-[#0a0a0a] leading-tight">
          {tabLabels[activeTab]}
        </h1>

        {/* Search bar + Filter/Sort button — enabled on every tab. Placeholder
         *  is identical across all three tabs for visual consistency; the
         *  underlying search clauses match the same fields on every tab
         *  (including @username, even though it's not mentioned in the
         *  placeholder copy). */}
        <div className="relative flex items-center gap-2.5">
          <div className="flex-1 flex items-center bg-white border rounded-lg shadow-sm px-2.5 py-2 gap-2 transition border-border-primary focus-within:border-text-brand">
            <Search size={20} className="text-text-tertiary shrink-0" />
            <input
              type="text"
              placeholder="Search by name, phone, ticket ID…"
              value={queryDraft}
              onChange={(e) => setQueryDraft(e.target.value)}
              maxLength={100}
              className="flex-1 text-sm text-text-primary placeholder:text-text-tertiary outline-none bg-transparent"
            />
            {queryDraft && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={clearActiveQuery}
                className="shrink-0 w-5 h-5 flex items-center justify-center rounded-full text-text-tertiary hover:text-text-secondary hover:bg-surface-tertiary"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => setPopoverOpen((o) => !o)}
            aria-label="Filter and sort"
            aria-expanded={popoverOpen}
            className={`relative shrink-0 h-9 w-9 flex items-center justify-center rounded-lg border bg-white shadow-sm transition ${
              filterActive
                ? 'border-text-brand text-text-brand'
                : 'border-border-primary text-text-tertiary hover:text-text-secondary'
            }`}
          >
            <SlidersHorizontal size={18} />
            {filterActive && (
              <span
                className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-text-brand"
                aria-hidden
              />
            )}
          </button>

          {popoverOpen && (
            <>
              {/* Backdrop — captures outside clicks to close */}
              <button
                type="button"
                aria-label="Close filter"
                className="fixed inset-0 z-20 bg-black/0"
                onClick={() => setPopoverOpen(false)}
              />
              {/* Popover panel */}
              <div className="absolute top-full right-0 mt-2 z-30 w-80 max-w-[calc(100vw-1.5rem)] bg-white border border-border-primary rounded-xl shadow-lg p-4 flex flex-col gap-4">
                {/* Sort section — options differ per tab. Resolved sorts
                    rows of users; other tabs sort rows of tickets. */}
                <div className="flex flex-col gap-2">
                  <div className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Sort by</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {activeTab === 'resolved'
                      ? (Object.keys(resolvedSortLabels) as ResolvedSort[]).map((key) => {
                          const isSel = resolvedSort === key;
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => setActiveResolvedSort(key)}
                              className={`h-8 px-3 rounded-lg text-sm font-medium transition border ${
                                isSel
                                  ? 'bg-text-brand text-white border-text-brand'
                                  : 'bg-white text-text-primary border-border-primary hover:border-text-brand'
                              }`}
                            >
                              {resolvedSortLabels[key]}
                            </button>
                          );
                        })
                      : (Object.keys(sortLabels) as TicketSort[]).map((key) => {
                          const isSel = activeSort === key;
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => setActiveSort(key)}
                              className={`h-8 px-3 rounded-lg text-sm font-medium transition border ${
                                isSel
                                  ? 'bg-text-brand text-white border-text-brand'
                                  : 'bg-white text-text-primary border-border-primary hover:border-text-brand'
                              }`}
                            >
                              {sortLabels[key]}
                            </button>
                          );
                        })}
                  </div>
                </div>

                {/* Date filter section — same control on every tab, label
                    swaps because the bound column differs (created_at vs
                    closed_at). */}
                <div className="flex flex-col gap-2">
                  <div className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
                    {activeTab === 'resolved' ? 'Resolved between' : 'Created'}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {DATE_PRESETS.map((preset) => (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => setActiveRange(presetToRange(preset.id))}
                        className="h-7 px-2.5 rounded-full text-xs font-medium bg-surface-tertiary text-text-secondary border border-border-secondary hover:border-text-brand hover:text-text-brand transition"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <label className="flex-1 flex flex-col gap-0.5">
                      <span className="text-[10px] text-text-tertiary">From</span>
                      <input
                        type="date"
                        value={activeRange.from ?? ''}
                        max={activeRange.to ?? undefined}
                        onChange={(e) => setActiveRange({ ...activeRange, from: e.target.value || undefined })}
                        className="h-8 px-2 text-sm border border-border-primary rounded-lg bg-white"
                      />
                    </label>
                    <label className="flex-1 flex flex-col gap-0.5">
                      <span className="text-[10px] text-text-tertiary">To</span>
                      <input
                        type="date"
                        value={activeRange.to ?? ''}
                        min={activeRange.from ?? undefined}
                        onChange={(e) => setActiveRange({ ...activeRange, to: e.target.value || undefined })}
                        className="h-8 px-2 text-sm border border-border-primary rounded-lg bg-white"
                      />
                    </label>
                  </div>
                </div>

                {/* Resolved-only: category multi-select. A row matches if
                    ANY of the user's resolved tickets carries any of the
                    selected categories. */}
                {activeTab === 'resolved' && allCategories.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <div className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Category</div>
                    <div className="flex flex-wrap gap-1.5">
                      {allCategories.map((cat) => {
                        const isSel = listCategories.includes(cat.id);
                        return (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => toggleCategory(cat.id)}
                            className={`h-7 px-2.5 rounded-full text-xs font-medium border transition ${
                              isSel
                                ? 'bg-text-brand text-white border-text-brand'
                                : 'bg-surface-tertiary text-text-secondary border-border-secondary hover:border-text-brand hover:text-text-brand'
                            }`}
                          >
                            {cat.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Resolved-only: re-engagement toggle. "Has follow-up" means
                    the user messaged after we closed their last ticket — a
                    signal they came back with something. */}
                {activeTab === 'resolved' && (
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-0.5">
                      <div className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Has follow-up</div>
                      <div className="text-[11px] text-text-tertiary">User messaged after their last close</div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={listHasFollowup}
                      onClick={() => setActiveHasFollowup(!listHasFollowup)}
                      className={`relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition ${
                        listHasFollowup ? 'bg-text-brand' : 'bg-surface-tertiary border border-border-secondary'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition shadow ${
                          listHasFollowup ? 'translate-x-5' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                )}

                <div className="flex items-center justify-between pt-2 border-t border-border-secondary">
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    disabled={!filterActive}
                    className={`text-xs font-medium ${
                      filterActive ? 'text-text-brand hover:underline' : 'text-text-tertiary cursor-default'
                    }`}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    onClick={() => setPopoverOpen(false)}
                    className="h-8 px-3 rounded-lg text-sm font-medium bg-text-brand text-white"
                  >
                    Done
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Filter chip row — visible on every tab whenever any filter
            dimension is non-default. Chips render per-tab; Resolved-only
            dimensions (category, has-followup) appear only when on Resolved. */}
        {filterActive && (
          <div className="flex flex-wrap items-center gap-1.5">
            {activeTab === 'resolved' && resolvedSort !== 'most_recent' && (
              <span className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1.5 rounded-full bg-surface-tertiary border border-border-secondary text-xs text-text-secondary">
                Sort: {resolvedSortLabels[resolvedSort]}
                <button
                  type="button"
                  aria-label="Clear sort"
                  onClick={() => setActiveResolvedSort('most_recent')}
                  className="ml-0.5 w-4 h-4 flex items-center justify-center rounded-full hover:bg-surface-secondary"
                >
                  <X size={12} />
                </button>
              </span>
            )}
            {activeTab !== 'resolved' && activeSort !== 'unread' && (
              <span className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1.5 rounded-full bg-surface-tertiary border border-border-secondary text-xs text-text-secondary">
                Sort: {sortLabels[activeSort]}
                <button
                  type="button"
                  aria-label="Clear sort"
                  onClick={() => setActiveSort('unread')}
                  className="ml-0.5 w-4 h-4 flex items-center justify-center rounded-full hover:bg-surface-secondary"
                >
                  <X size={12} />
                </button>
              </span>
            )}
            {(activeRange.from || activeRange.to) && (
              <span className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1.5 rounded-full bg-surface-tertiary border border-border-secondary text-xs text-text-secondary">
                {formatRangeLabel(activeRange)}
                <button
                  type="button"
                  aria-label="Clear date range"
                  onClick={() => setActiveRange({})}
                  className="ml-0.5 w-4 h-4 flex items-center justify-center rounded-full hover:bg-surface-secondary"
                >
                  <X size={12} />
                </button>
              </span>
            )}
            {activeTab === 'resolved' && listCategories.map((id) => (
              <span key={id} className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1.5 rounded-full bg-surface-tertiary border border-border-secondary text-xs text-text-secondary">
                {categoryLabelById.get(id) ?? id}
                <button
                  type="button"
                  aria-label={`Clear category ${id}`}
                  onClick={() => toggleCategory(id)}
                  className="ml-0.5 w-4 h-4 flex items-center justify-center rounded-full hover:bg-surface-secondary"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
            {activeTab === 'resolved' && listHasFollowup && (
              <span className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1.5 rounded-full bg-surface-tertiary border border-border-secondary text-xs text-text-secondary">
                Has follow-up
                <button
                  type="button"
                  aria-label="Clear has-followup filter"
                  onClick={() => setActiveHasFollowup(false)}
                  className="ml-0.5 w-4 h-4 flex items-center justify-center rounded-full hover:bg-surface-secondary"
                >
                  <X size={12} />
                </button>
              </span>
            )}
            {committedQuery.trim() && (
              <span className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1.5 rounded-full bg-surface-tertiary border border-border-secondary text-xs text-text-secondary max-w-[60vw]">
                <span className="truncate">Search: “{committedQuery.trim()}”</span>
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={clearActiveQuery}
                  className="ml-0.5 w-4 h-4 flex items-center justify-center rounded-full hover:bg-surface-secondary shrink-0"
                >
                  <X size={12} />
                </button>
              </span>
            )}
            <button
              type="button"
              onClick={clearAllFilters}
              className="text-xs text-text-brand hover:underline ml-1"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Tab Navigation — click only, no swipe.
            Sized so 3-digit counts (e.g. 345) still fit inside each ⅓ slot
            on a 320px viewport without pushing the badge past the rounded
            container border, while letting "New Tickets" / "In Progress"
            wrap to two lines naturally:
              - min-h-9 (not h-9) so the row grows when labels wrap
              - tighter horizontal padding (px-2 / gap-1.5) gives the badge room
              - min-w-0 on the button so flex doesn't push children out
              - text-center on the label so each wrapped line stays centered
              - shrink-0 on the badge so a long label gives way first
              - tabular-nums on the badge so 100s/10s digits don't wobble */}
        <div className="bg-surface-tertiary border border-border-secondary rounded-xl p-1 flex items-stretch gap-1">
          {tabs.map(tab => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => changeTab(tab)}
                className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 min-h-9 px-2 py-2 rounded-lg text-sm font-medium leading-tight transition ${
                  isActive
                    ? 'bg-white text-text-primary shadow-sm'
                    : 'text-text-tertiary hover:text-text-secondary'
                }`}
              >
                <span className="text-center">{tabLabels[tab]}</span>
                {tabCounts[tab] !== undefined && (
                  <span
                    className="shrink-0 bg-badge-gray-bg border border-badge-gray-border text-text-secondary text-xs font-medium px-1.5 py-0.5 rounded-full leading-4 tabular-nums"
                    title={`${tabCounts[tab]}`}
                  >
                    {/* Truncate 4+ digit counts so the badge keeps fitting
                     *  inside the rounded container at every viewport width.
                     *  Real count surfaces via the tooltip on hover. */}
                    {(tabCounts[tab] as number) > 999 ? '999+' : tabCounts[tab]}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* List scrolls; pagination sits flush above a thin safe-area gutter */}
      <div className="flex-1 flex flex-col min-h-0 gap-0">
        <div
          className="relative flex-1 min-h-0 overflow-y-auto overflow-x-hidden -mx-1 px-1"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Skeletons cover two states:
              - Initial load with no current data (isLoading && empty)
              - Pagination in flight (user just clicked next/prev)
             A silent background refresh (isLoading on a populated list, no
             pager click) keeps the current rows — no skeleton flicker for
             routine WS/polling updates. */}
          {pagingInFlight || (isLoading && emptyForTab) ? (
            <TicketRowSkeletonList />
          ) : emptyForTab && !isLoading && !pagingInFlight ? (
            <div
              key={activeTab}
              className={`flex min-h-[40vh] items-center justify-center text-center py-12 text-text-tertiary ${slideDir === 'right' ? 'animate-slide-in-from-right' : 'animate-slide-in-from-left'}`}
            >
              <p>No {tabLabels[activeTab].toLowerCase()} found</p>
            </div>
          ) : (
            <div
              key={activeTab}
              className={`flex flex-col gap-1 pb-2 ${slideDir === 'right' ? 'animate-slide-in-from-right' : 'animate-slide-in-from-left'}`}
            >
              {/* Pinned subset gets wrapped in a single SortableContext per
               *  tab. Server sorts pinned rows first, so we slice the prefix.
               *  Unpinned rows render plain (drag isn't meaningful for them). */}
              {(() => {
                const externalBadgeFor = (t: Ticket) => {
                  // Partner-source tag (SimVerifier / Wallet). Prefer the
                  // normalized Phase 36 caseType, fall back to the raw source so
                  // legacy rows ('simverifier' / 'wl' / etc.) still tag. Case-
                  // insensitive. Organic tickets (no source) get no badge.
                  const code = (t.externalCaseType || t.externalSource || '').toLowerCase();
                  if (['sv', 'simverifier'].includes(code)) return [{ label: 'SV', variant: 'gray' as const }];
                  if (['wl', 'wallet'].includes(code)) return [{ label: 'WL', variant: 'gray' as const }];
                  return undefined;
                };

                if (activeTab === 'resolved') {
                  const rows = Array.isArray(archivedConversations) ? archivedConversations : [];
                  const pinnedRows = rows.filter((r) => r.ticket.isPinned);
                  const unpinnedRows = rows.filter((r) => !r.ticket.isPinned);
                  const renderResolvedRow = (row: typeof rows[number], inSortable: boolean) => {
                    const badges = [
                      // "Active" badge on the Resolved row when the user also has
                      // currently-active tickets (open/in_progress/escalated)
                      // elsewhere. Tells the tech this row is closed history,
                      // but the user is still engaged in other open work — so
                      // they can navigate to active tabs to find those rows.
                      ...(row.hasActive ? [{ label: 'Active', variant: 'brand' as const }] : []),
                      ...(row.resolvedCount > 1 ? [{ label: `+${row.resolvedCount - 1}`, variant: 'gray' as const }] : []),
                      ...(externalBadgeFor(row.ticket) ?? []),
                    ];
                    const matched = row.matchedTicket;
                    const matchLabels: string[] = [];
                    if (matched) {
                      matchLabels.push(matched.ticketId);
                      if (matched.closedAt) matchLabels.push(formatMatchedDate(matched.closedAt));
                      matched.categories.forEach((catId) => {
                        const label = categoryLabelById.get(catId) ?? catId;
                        if (label) matchLabels.push(label);
                      });
                    }
                    const matchLine = matched && matchLabels.length > 0 ? (
                      <div
                        className="text-[10.5px] leading-[14px] text-text-tertiary pl-3 pr-2"
                        title={`Matched ticket: ${matched.ticketId}`}
                      >
                        ↳ Matched: {matchLabels.join(' · ')}
                        {row.matchedCount > 1 && row.matchedCount < row.resolvedCount && (
                          <span className="text-text-secondary"> · +{row.matchedCount - 1} more</span>
                        )}
                      </div>
                    ) : null;

                    if (inSortable) {
                      return (
                        <SortableTicketRow
                          key={row.ticket.ticketId}
                          id={row.ticket.ticketId}
                          ticket={row.ticket}
                          extraBadges={badges.length > 0 ? badges : undefined}
                          onClick={() => onSelectTicket(row.ticket)}
                          onPin={() => handlePin(row.ticket.ticketId)}
                          onUnpin={() => handleUnpin(row.ticket.ticketId)}
                          pinning={pinningIds.has(row.ticket.ticketId)}
                          belowRow={matchLine}
                        />
                      );
                    }
                    return (
                      <div key={`u-${row.ticket.userId}`} className="flex flex-col gap-0.5">
                        <TicketRowItem
                          ticket={row.ticket}
                          extraBadges={badges.length > 0 ? badges : undefined}
                          onClick={() => onSelectTicket(row.ticket)}
                          onPin={() => handlePin(row.ticket.ticketId)}
                          onUnpin={() => handleUnpin(row.ticket.ticketId)}
                          pinning={pinningIds.has(row.ticket.ticketId)}
                        />
                        {matchLine}
                      </div>
                    );
                  };

                  return (
                    <>
                      {pinnedRows.length > 0 && (
                        <DndContext
                          sensors={dndSensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handlePinDragEnd}
                        >
                          <SortableContext
                            items={pinnedRows.map((r) => r.ticket.ticketId)}
                            strategy={verticalListSortingStrategy}
                          >
                            {pinnedRows.map((row) => renderResolvedRow(row, true))}
                          </SortableContext>
                        </DndContext>
                      )}
                      {pinnedRows.length > 0 && unpinnedRows.length > 0 && (
                        <div className="my-1 h-px bg-border-secondary/50" aria-hidden />
                      )}
                      {unpinnedRows.map((row) => renderResolvedRow(row, false))}
                    </>
                  );
                }

                // New / In-Progress tabs — ticket-row tabs.
                const pinnedTickets = ticketsForTab.filter((t) => t.isPinned);
                const unpinnedTickets = ticketsForTab.filter((t) => !t.isPinned);
                return (
                  <>
                    {pinnedTickets.length > 0 && (
                      <DndContext
                        sensors={dndSensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handlePinDragEnd}
                      >
                        <SortableContext
                          items={pinnedTickets.map((t) => t.ticketId)}
                          strategy={verticalListSortingStrategy}
                        >
                          {pinnedTickets.map((ticket) => (
                            <SortableTicketRow
                              key={ticket.ticketId}
                              id={ticket.ticketId}
                              ticket={ticket}
                              extraBadges={externalBadgeFor(ticket)}
                              onClick={() => onSelectTicket(ticket)}
                              onPin={() => handlePin(ticket.ticketId)}
                              onUnpin={() => handleUnpin(ticket.ticketId)}
                              pinning={pinningIds.has(ticket.ticketId)}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    )}
                    {pinnedTickets.length > 0 && unpinnedTickets.length > 0 && (
                      <div className="my-1 h-px bg-border-secondary/50" aria-hidden />
                    )}
                    {unpinnedTickets.map((ticket) => (
                      <TicketRowItem
                        key={ticket.ticketId}
                        ticket={ticket}
                        extraBadges={externalBadgeFor(ticket)}
                        onClick={() => onSelectTicket(ticket)}
                        onPin={() => handlePin(ticket.ticketId)}
                        onUnpin={() => handleUnpin(ticket.ticketId)}
                        pinning={pinningIds.has(ticket.ticketId)}
                      />
                    ))}
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {(showOpenPager || showResolvedPager) && (
        <div className="shrink-0 flex w-full flex-col border-t border-border-secondary/60 bg-surface-secondary">
          {/* Fixed-height bar — controls centered; safe area is NOT padding here */}
          <div className="flex min-h-10 w-full items-center px-2 py-1.5 sm:px-3">
            {showOpenPager && (
              <Pagination
                compact
                page={openPagination.page}
                totalPages={openPagination.totalPages}
                onPageChange={handleOpenPageChange}
              />
            )}

            {showResolvedPager && (
              <Pagination
                compact
                page={archivedPagination.page}
                totalPages={archivedPagination.totalPages}
                onPageChange={handleResolvedPageChange}
              />
            )}
          </div>
          {/* iPhone home indicator — empty strip only; keeps pagination row visually centered */}
          <div
            className="shrink-0 bg-surface-secondary"
            style={{ height: 'env(safe-area-inset-bottom, 0px)' }}
            aria-hidden
          />
        </div>
        )}
      </div>
    </div>
  );
}
